/*
 * -----------------------------------------------------------------------
 *  <copyright file="reason-change-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InputLabel from '@mui/material/InputLabel';
import React, { useState, useCallback, useEffect } from 'react';

import { useTranslation, useBoolean } from '../../../../hooks';
import { validateFields } from '../../../../utils/validation';
import { SaveButton } from '../buttons';
import CharactersRemaining from '../characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from '../dialog';
import TextField from '../text-field';

import './styles.scss';

const ReasonForChangeDialog = ({ open, onClose }: any) => {
    const { t } = useTranslation();
    const [reasonError, setReasonError] = useState(null);
    const [reason, setReason] = useState('');

    const disabledSave = useBoolean(true);

    const close = (value?: string | null) => {
        setReasonError(null);
        setReason('');
        onClose(value);
    };

    const escFunction = useCallback((event) => {
        if (event.key === 'Escape') {
            onClose(null);
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', escFunction, false);

        return () => {
            document.removeEventListener('keydown', escFunction, false);
        };
    }, []);

    return (
        <CustomDialog disableEscapeKeyDown onClose={close} fullWidth scroll="paper" maxWidth="md" open={open}>
            <DialogTitle id="reason-change-title">
                {t('ra:reasonForChange')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => {
                        close();
                    }}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent style={{ paddingLeft: 20, paddingRight: 20, paddingTop: 20 }}>
                <TextField
                    label={`${t('ra:reasonForChange')}*`}
                    id="reason-field"
                    multiline
                    rows={5}
                    fullWidth
                    value={reason}
                    onChange={(event: any) => {
                        setReasonError(null);
                        setReason(event.target.value);
                        disabledSave.setFalse();
                        const error = validateFields(event.target.value, 'reason', 4000, false);
                        setReasonError(error?.reason ?? null);
                    }}
                    size="small"
                    placeholder={t('ra:reasonChangeHelperText')}
                    helperText={
                        <CharactersRemaining
                            id="helper-reason-field"
                            length={reason.length}
                            max={4000}
                            error={reasonError}
                        />
                    }
                    variant="outlined"
                    color="secondary"
                    className={`${4000 - reason.length < 0 ? 'mb red-border' : 'mb'}`}
                />
            </DialogContent>
            <DialogActions className="actions-add-risk assign-items flex-end">
                <SaveButton
                    className="edit-cv-update-btn"
                    id="reason-change-save"
                    text="save"
                    disabled={reasonError || disabledSave.value}
                    onClick={() => {
                        const error = validateFields(reason, 'reason', 4000, true);
                        setReasonError(error.reason);
                        if (error?.reason || !reason.length) return;

                        close(reason);
                    }}
                />
            </DialogActions>
        </CustomDialog>
    );
};

export default ReasonForChangeDialog;
